import {inject, Injectable, Injector} from "@angular/core"
import {Currency, PriceGraphState} from "@generated"
import {UncachedEvaluator} from "@cm/graph-v3"

//It has happened that some nodes were not registered in the factory after graph deserialization, so we import them here to make sure they are registered
import "@cm/pricing/register-nodes"
import {aggregateMeshEntries, ConfiguratorVariant, PriceScope, PriceSelector, PricedItem, PricingContext, PricingResult, SkuEntry} from "@cm/pricing"
import {loadPricingData, PricingData} from "@pricing/helpers"
import {PriceGraphAccessor} from "@pricing/models/pricing/price-graph"
import {PricingServiceGetTemplateGQL} from "@pricing/services/pricing.generated"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {getTemplateNodeLabel, isConfigInput, MeshNode, SceneNodes} from "@cm/template-nodes"

@Injectable({
    providedIn: "root",
})
export class PricingService {
    templateId: string | undefined = undefined
    organizationId: string | undefined = undefined
    sceneManagerService: SceneManagerService | undefined = undefined

    priceGraphAccessor: PriceGraphAccessor | undefined = undefined
    private pricingData: PricingData | undefined = undefined

    readonly injector = inject(Injector)
    readonly templateGql = inject(PricingServiceGetTemplateGQL)

    setup(templateId: string, organizationId: string, sceneManagerService: SceneManagerService) {
        this.pricingData = undefined
        this.priceGraphAccessor = undefined

        this.templateId = templateId
        this.organizationId = organizationId
        this.sceneManagerService = sceneManagerService
    }

    async getPrices(selector?: PriceSelector): Promise<PricingResult> {
        await this.load()
        if (!this.priceGraphAccessor || !this.pricingData) {
            return {items: [], priceIncludesVat: true}
        }

        const resolvedCurrency = this.resolveCurrency(this.pricingData.availableCurrencies, selector?.currency)
        const context = new PricingContext(this.pricingData.priceMap, resolvedCurrency)
        const items = await this.getPricesForMappingData(this.priceGraphAccessor, context)
        return {items, priceIncludesVat: context.getPriceIncludesVat()}
    }

    async getSkus(): Promise<SkuEntry[]> {
        await this.load()
        if (!this.priceGraphAccessor || !this.pricingData) {
            return []
        }

        // sku is currency-independent (currency only feeds price lookup, not which nodes run),
        // so any available currency yields the same set of article numbers.
        const currency = this.pricingData.availableCurrencies[0]
        if (currency === undefined) return []

        const context = new PricingContext(this.pricingData.priceMap, currency)
        const items = await this.getPricesForMappingData(this.priceGraphAccessor, context)

        const merged = new Map<string, number>()
        for (const item of items) {
            if (item.sku === undefined) continue
            merged.set(item.sku, (merged.get(item.sku) ?? 0) + item.amount)
        }
        return [...merged].map(([sku, amount]) => ({sku, amount}))
    }

    async getPriceScopes(): Promise<PriceScope[]> {
        await this.load()
        if (!this.pricingData) return []
        return this.pricingData.availableCurrencies.map((currency) => ({currency}))
    }

    async getPricesForMappingData(graphAccessor: PriceGraphAccessor, pricingContext: PricingContext): Promise<PricedItem[]> {
        this.setSelectedVariants(graphAccessor)
        this.setCurrentMeshes(graphAccessor)
        return new UncachedEvaluator(pricingContext).get(graphAccessor.rootNode)
    }

    static computeTotalPrice(prices: PricedItem[]): number {
        return prices.reduce((acc, item) => acc + item.price * item.amount, 0)
    }

    private resolveCurrency(available: Currency[], requested: Currency | undefined): Currency {
        if (available.length === 0) {
            throw new Error("No prices loaded for this configurator")
        }
        if (requested !== undefined) {
            return requested
        }
        if (available.length === 1) {
            return available[0]
        }
        throw new Error(`Currency must be specified — available currencies: ${available.join(", ")}`)
    }

    private async load() {
        if (!this.templateId || !this.organizationId) {
            return
        }
        if (this.pricingData && this.priceGraphAccessor) {
            return
        }

        const pricingData = await loadPricingData(this.injector, this.organizationId, this.templateId, PriceGraphState.Ready)
        if (!pricingData) return

        this.priceGraphAccessor = new PriceGraphAccessor(pricingData.rootNode)
        this.pricingData = pricingData
    }

    private setSelectedVariants(priceGraphAccessor: PriceGraphAccessor) {
        const selectedVariants: ConfiguratorVariant[] = []

        this.sceneManagerService?.getDescriptors().forEach((desc) => {
            if (isConfigInput(desc) && desc.props.value) {
                selectedVariants.push({
                    groupId: desc.props.id,
                    groupName: desc.props.name,
                    variantId: desc.props.value.id, //this is the value that is selected in the ui, i.e. the crucial part
                    variantName: desc.props.variants.find((v) => v.id === desc.props.value?.id)?.name ?? "unknown",
                })
            }
        })

        priceGraphAccessor.setCurrentConfigurations(selectedVariants)
    }

    private setCurrentMeshes(priceGraphAccessor: PriceGraphAccessor) {
        const scene = this.sceneManagerService?.$scene() ?? []
        const meshes = scene.filter(SceneNodes.Mesh.is).flatMap((meshNode) => {
            const node = meshNode.path.at(-1)
            if (!(node instanceof MeshNode)) return []
            return [{name: node.parameters.name, path: meshNode.path.map((n) => getTemplateNodeLabel(n)).join(" / ")}]
        })
        priceGraphAccessor.setCurrentMeshes(aggregateMeshEntries(meshes))
    }
}
